import { Component, ElementRef, HostListener, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { NgxTgModule } from '@worthy-ventures/metaglotta-ngx';

@Component({
  selector: 'app-about-us',
  imports: [MenuBarComponent, FooterComponent, NgxTgModule],
  templateUrl: './about-us.component.html',
  styleUrl: './about-us.component.scss'
})
export class AboutUsComponent {
  @ViewChildren('chapterEl') chapterEls!: QueryList<ElementRef<HTMLElement>>;

  // The company story, split at the dates the copy itself names. Every value
  // is a translation key, resolved with | translate in the template. The
  // opening paragraph (about_s1_p1) belongs to no chapter: it is set as a lead
  // at the top of the first one.
  readonly chapters = [
    {
      year: 'about_s1_ch1_year',
      title: 'about_s1_ch1_title',
      paragraphs: ['about_s1_p2', 'about_s1_p3', 'about_s1_p4'],
    },
    {
      year: 'about_s1_ch2_year',
      title: 'about_s1_ch2_title',
      paragraphs: ['about_s1_p5'],
    },
    {
      year: 'about_s1_ch3_year',
      title: 'about_s1_ch3_title',
      paragraphs: ['about_s1_p6', 'about_s1_p7'],
    },
    {
      year: 'about_s1_ch4_year',
      title: 'about_s1_ch4_title',
      paragraphs: ['about_s1_p8'],
    },
  ];
  activeChapter = 0;

  // Lights that breathe over the three images. x/y are in each image's own
  // pixels; the SCSS turns them into positions inside the frame the page crops
  // that image to, so they stay on their bulb at every width. Delays are
  // negative so each light starts mid-breath rather than all at once.

  // The fingertip flare in the story photo. It swells over the flare already
  // in the photo: dimming it there would smear the finger and tablet edge.
  readonly storyLights = [
    { x: 503, y: 585, kind: 'flare', delay: -1.2, duration: 4.5 },
  ];

  // The bulbs in the technology image (about-technology.webp), which has them
  // turned down so each one fades from dim to lit. The cluster's red pair (and
  // the aura around it) and its white pair run half a cycle apart. The two
  // soft glows by "SMART HOME" are left at full strength in the photo and swell.
  readonly techLights = [
    { x: 233, y: 276, kind: 'red', delay: -0.2, duration: 3.6 },
    { x: 340, y: 304, kind: 'red', delay: -1.9, duration: 4.2 },
    { x: 372, y: 449, kind: 'red', delay: -3.1, duration: 3.9 },
    { x: 473, y: 422, kind: 'red-halo', delay: -0.8, duration: 3.2 },
    { x: 472, y: 414, kind: 'red', delay: -0.8, duration: 3.2 },
    { x: 484, y: 422, kind: 'red', delay: -0.8, duration: 3.2 },
    { x: 463, y: 423, kind: 'white', delay: -2.4, duration: 3.2 },
    { x: 473, y: 428, kind: 'white', delay: -2.4, duration: 3.2 },
    { x: 608, y: 510, kind: 'red', delay: -2.6, duration: 4.4 },
    { x: 787, y: 454, kind: 'red', delay: -1.2, duration: 3.8 },
    { x: 159, y: 433, kind: 'red-glow', delay: -1.5, duration: 5.2 },
    { x: 190, y: 449, kind: 'green-glow', delay: -4.0, duration: 5.2 },
  ];

  // The markers on the partners map, which the image leaves out. All on one
  // 4s beat: Greece pulses and sends out a ring, then each partner peaks a
  // little later the further its arc is (0.5s + 1.3s x distance / farthest),
  // so the light reads as travelling outwards from Athens.
  readonly partnerLights = [
    { x: 849, y: 368, kind: 'ping', delay: 0, duration: 4 },
    { x: 849, y: 368, kind: 'hub', delay: -2, duration: 4 },
    { x: 769, y: 330, kind: 'marker', delay: -1.3, duration: 4 },   // France
    { x: 770, y: 423, kind: 'marker', delay: -1.25, duration: 4 },  // Algeria
    { x: 1113, y: 208, kind: 'marker', delay: -0.75, duration: 4 }, // Russia
    { x: 1166, y: 369, kind: 'marker', delay: -0.75, duration: 4 }, // China
    { x: 1179, y: 532, kind: 'marker', delay: -0.6, duration: 4 },  // Malaysia
    { x: 1304, y: 665, kind: 'marker', delay: -0.2, duration: 4 },  // Australia
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  // The chapter being read is the last one whose top has passed 40% of the
  // viewport. Reading positions rather than intersections keeps a short
  // chapter (the one-paragraph "today") selectable after a jump to it.
  @HostListener('window:scroll')
  onScroll(): void {
    const line = window.innerHeight * 0.4;
    let active = 0;
    this.chapterEls.forEach((el, i) => {
      if (el.nativeElement.getBoundingClientRect().top <= line) {
        active = i;
      }
    });
    this.activeChapter = active;
  }

  goToChapter(index: number): void {
    this.chapterEls.get(index)?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  goToUrl(url: string): void {
    this.router.navigate(['/' + url]);
  }
}
